import {createApiClient} from './client';

/**
 The Data-owned reporting service (apps/reporting-api). These types are the TypeScript reading of
 contracts/reporting/report-spec.v1.json: a report is a list of typed blocks, each carrying the rows
 its query returned. The browser only ever sends named parameters; the SQL stays server-side.
 */

export type NumberFormat = 'integer' | 'decimal' | 'percent';

// One choice of a select parameter: what is sent (`value`) and what the analyst reads (`label`).
export type ReportOption = {value: string; label: string};

export type ReportParameter = {
  name: string;
  // A `date` is picked on a calendar. A `select` is one choice from `options`, or all of them; a
  // `multiselect` is any number of them (a comma-separated value, none meaning all); a `toggle` is
  // exactly one of a few fixed `options`, such as Actor or Content, and is never all.
  type: 'date' | 'select' | 'multiselect' | 'toggle';
  // Not shown and not changeable: the service always uses its default (a fixed six-month window).
  hidden?: boolean;
  label?: string;
  // A date's ISO date or day/month offset such as `-30d` or `-5m`, resolved by the service when the
  // request omits it; a toggle's is the option it starts on. A select has none: it starts at all, and
  // an omitted value is how "all" is sent.
  default?: string;
  // The choices of a select or multiselect come with a report's response, not with the list of
  // reports. A toggle's are fixed, so they are in both.
  options?: ReportOption[];
};

export type FieldRef = {field: string; label: string};

export type ReportColumn = FieldRef & {
  align?: 'left' | 'right';
  format?: NumberFormat;
};

export type RowValue = string | number | boolean | null;
export type ReportRow = Record<string, RowValue>;

type BlockCommon = {
  id: string;
  title: string;
  description?: string;
  // `half` sits the block beside the next half-width one.
  width?: 'full' | 'half';
};

export type KpiThresholds = {
  // Which way a value gets worse; the service default is `higher_is_worse`.
  direction?: 'higher_is_worse' | 'lower_is_worse';
  warning?: number;
  error?: number;
};

export type ReportKpiBlock = BlockCommon & {
  type: 'kpi';
  value_field: string;
  format?: NumberFormat;
  caption?: string;
  thresholds?: KpiThresholds;
  value: number | null;
};

// One tile of a multi-value KPI: a column of the query's single row with its own label and format.
export type KpiMetric = {
  field: string;
  label: string;
  format?: NumberFormat;
  thresholds?: KpiThresholds;
  value: number | null;
};

export type ReportKpiGridBlock = BlockCommon & {
  type: 'kpi';
  metrics: KpiMetric[];
  caption?: string;
};

// A block's `type` is the view it arrives in; `views`, when present, lists every view the reader may
// switch to (the default included). The rows are the same in each, so switching never refetches.
export type SeriesChartView = 'line_chart' | 'bar_chart' | 'scatter_chart';
export type DistributionView = 'pie_chart' | 'linear_bar_chart';

export type ReportSeriesChartBlock = BlockCommon & {
  type: SeriesChartView;
  x: FieldRef;
  series: FieldRef[];
  stacked?: boolean;
  views?: Array<SeriesChartView | 'table'>;
  rows: ReportRow[];
};

export type ReportDistributionBlock = BlockCommon & {
  type: DistributionView;
  category: FieldRef;
  value: FieldRef;
  format?: NumberFormat;
  views?: Array<DistributionView | 'table'>;
  rows: ReportRow[];
};

export type ReportTableBlock = BlockCommon & {
  type: 'table';
  columns: ReportColumn[];
  rows: ReportRow[];
};

// Long-form rows pivoted into a grid: a grid row per `row` value, a column per `column` value.
export type ReportMatrixBlock = BlockCommon & {
  type: 'matrix';
  row: FieldRef;
  // `returned` keeps columns in query order, for axes that share one list (a confusion matrix).
  column: FieldRef & {format?: 'month'; order?: 'sorted' | 'returned'};
  value: FieldRef;
  format?: NumberFormat;
  heatmap?: boolean;
  // The `row` value that totals or averages the others: emphasised, never shaded.
  total_row?: string;
  rows: ReportRow[];
};

export type ReportBlock =
  | ReportKpiBlock
  | ReportKpiGridBlock
  | ReportSeriesChartBlock
  | ReportDistributionBlock
  | ReportTableBlock
  | ReportMatrixBlock;
export type ReportBlockType = ReportBlock['type'];

export type ReportSummary = {
  id: string;
  title: string;
  description?: string;
  required_permission?: string;
  parameters?: ReportParameter[];
};

export type Report = ReportSummary & {
  parameters: ReportParameter[];
  // What the queries actually ran with, after the service applied defaults.
  parameter_values: Record<string, string>;
  generated_at: string;
  blocks: ReportBlock[];
};

/** Parameter values as sent: one string per declared parameter name. */
export type ReportQuery = Record<string, string>;

// Same token and 401 handling as the API client, at the reporting service's own prefix. The Vite
// proxy (and, deployed, nginx) forward the path untouched, so no rewriting happens on either side.
const reporting = createApiClient('/reporting-api');

export const reportingApi = {
  async list() {
    const {data} = await reporting.get<ReportSummary[]>('/reports');
    return data;
  },
  async get(id: string, parameters?: ReportQuery) {
    const {data} = await reporting.get<Report>(`/reports/${encodeURIComponent(id)}`, {
      params: parameters
    });
    return data;
  }
};
